// components/workspace/WorkspaceSwitcher.js - Updated with rename functionality
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Plus, Building2, Check, Crown, Lock, Edit2 } from 'lucide-react';

const WorkspaceSwitcher = ({ 
  currentWorkspace, 
  workspaces = [], 
  onWorkspaceSwitch,
  onCreateWorkspace,
  onRenameWorkspace, // New prop for rename callback
  userSubscription = null,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Enhanced subscription checks
  const hasWorkspaceAccess = userSubscription && ['SCALE', 'ENTERPRISE'].includes(userSubscription.plan);
  const canCreateWorkspaces = hasWorkspaceAccess && workspaces.length < getWorkspaceLimit();

  // Get workspace limit based on plan
  function getWorkspaceLimit() {
    if (!userSubscription) return 1;
    
    switch (userSubscription.plan) {
      case 'SCALE': return 5;
      case 'ENTERPRISE': return 20;
      default: return 1;
    }
  }

  // Check if workspace switching should be restricted
  const isSwitchingRestricted = !hasWorkspaceAccess && workspaces.length > 1;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitchWorkspace = async (workspace) => {
    if (workspace.id === currentWorkspace?.id) {
      setIsOpen(false);
      return;
    }

    if (isSwitchingRestricted) {
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      await onWorkspaceSwitch(workspace);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to switch workspace:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    if (!hasWorkspaceAccess || !canCreateWorkspaces) {
      setIsOpen(false);
      return;
    }
    
    setIsOpen(false);
    onCreateWorkspace();
  };

  const handleRename = (workspace, event) => {
    event.stopPropagation(); // Prevent workspace switching
    setIsOpen(false);
    if (onRenameWorkspace) {
      onRenameWorkspace(workspace);
    }
  };

  const handleUpgradeClick = () => {
    setIsOpen(false);
    window.location.href = '/pricing';
  };

  if (!currentWorkspace) {
    return (
      <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-100 ${className}`}>
        <div className="w-4 h-4 bg-gray-300 rounded animate-pulse"></div>
        <div className="w-24 h-4 bg-gray-300 rounded animate-pulse"></div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-colors ${
          isOpen 
            ? 'bg-purple-50 border-purple-200 text-purple-700' 
            : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'
        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <div className="w-6 h-6 bg-purple-100 rounded-md flex items-center justify-center">
          <Building2 className="w-4 h-4 text-purple-600" />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-sm font-medium truncate">
            {currentWorkspace.name}
          </div>
          {currentWorkspace.description && (
            <div className="text-xs text-gray-500 truncate">
              {currentWorkspace.description}
            </div>
          )}
        </div>
        {isSwitchingRestricted && (
          <Crown className="w-4 h-4 text-amber-500" />
        )}
        <ChevronDown className={`w-4 h-4 transition-transform ${
          isOpen ? 'rotate-180' : ''
        }`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            
            {/* Upgrade Banner for non-premium users */}
            {!hasWorkspaceAccess && workspaces.length > 1 && (
              <div className="p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-t-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Crown className="h-4 w-4" />
                  <span className="font-medium text-sm">Upgrade to Scale</span>
                </div>
                <p className="text-xs text-purple-100 mb-3">
                  Access all your workspaces and unlock premium features
                </p>
                <button 
                  onClick={handleUpgradeClick}
                  className="text-xs bg-white text-purple-600 px-3 py-1.5 rounded font-medium hover:bg-gray-50 transition-colors"
                >
                  Upgrade Now
                </button>
              </div>
            )}

            {/* Workspace List */}
            <div className="max-h-64 overflow-y-auto">
              {workspaces.map((workspace) => {
                const isLocked = isSwitchingRestricted && workspace.id !== currentWorkspace.id;
                const isCurrent = currentWorkspace.id === workspace.id;
                const canRename = isCurrent && workspace.role === 'ADMIN' && onRenameWorkspace;
                
                return (
                  <div
                    key={workspace.id}
                    className={`group relative ${isCurrent ? 'bg-purple-50' : ''}`}
                  >
                    <button
                      onClick={() => handleSwitchWorkspace(workspace)}
                      disabled={loading || isLocked}
                      className={`w-full px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                        loading || isLocked ? 'opacity-50 cursor-not-allowed' : ''
                      } ${isCurrent ? 'bg-purple-50 hover:bg-purple-100' : ''} ${
                        canRename ? 'pr-12' : '' // Add padding for rename button
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isCurrent ? 'bg-purple-100' : 'bg-gray-100'
                          }`}>
                            {isLocked ? (
                              <Lock className="w-4 h-4 text-gray-400" />
                            ) : (
                              <Building2 className={`w-4 h-4 ${
                                isCurrent ? 'text-purple-600' : 'text-gray-600'
                              }`} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <span className={`text-sm font-medium truncate ${
                                isLocked ? 'text-gray-400' : 'text-gray-900'
                              }`}>
                                {workspace.name}
                              </span>
                              {isCurrent && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                  Current
                                </span>
                              )}
                            </div>
                            {workspace.description && (
                              <div className={`text-xs mt-0.5 truncate ${
                                isLocked ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                {workspace.description}
                              </div>
                            )}
                            {isLocked && (
                              <div className="flex items-center space-x-1 mt-1">
                                <Crown className="w-3 h-3 text-amber-500" />
                                <span className="text-xs text-amber-600">Premium</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          {isCurrent && !isLocked && !canRename && (
                            <Check className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Rename button for current workspace (admin only) */}
                    {canRename && (
                      <button
                        onClick={(e) => handleRename(workspace, e)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1.5 hover:bg-purple-200 rounded-md z-10"
                        title="Rename workspace"
                      >
                        <Edit2 className="w-3.5 h-3.5 text-purple-600" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Create New Workspace */}
            <div className="border-t border-gray-100">
              <button
                onClick={handleCreateNew}
                disabled={loading || !hasWorkspaceAccess || !canCreateWorkspaces}
                className={`w-full px-4 py-3 text-left transition-colors ${
                  loading || !hasWorkspaceAccess || !canCreateWorkspaces
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center border-2 border-dashed ${
                    hasWorkspaceAccess && canCreateWorkspaces 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-gray-300 bg-gray-50'
                  }`}>
                    {hasWorkspaceAccess && canCreateWorkspaces ? (
                      <Plus className="w-4 h-4 text-green-600" />
                    ) : (
                      <Lock className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className={`text-sm font-medium flex items-center ${
                      hasWorkspaceAccess && canCreateWorkspaces ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      Create Workspace
                      {!hasWorkspaceAccess && (
                        <Crown className="w-4 h-4 text-amber-500 ml-2" />
                      )}
                    </div>
                    <div className={`text-xs mt-0.5 ${
                      hasWorkspaceAccess && canCreateWorkspaces ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {!hasWorkspaceAccess 
                        ? 'Available on Scale plan'
                        : !canCreateWorkspaces
                          ? `${workspaces.length}/${getWorkspaceLimit()} limit reached`
                          : 'Start fresh with a new workspace'
                      }
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Simple Footer */}
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 rounded-b-lg">
              <div className="flex items-center justify-center text-xs text-gray-500">
                <span className="capitalize">{userSubscription?.plan?.toLowerCase() || 'Free'} Plan</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WorkspaceSwitcher;